import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { socialDetailsStep } from '@/pages/auth/signup/signupEntry';
import { useGlobalStoreSelector } from '@/stores/global.store';

/**
 * A social tap creates the User before the signup is finished, so an incomplete User holds a real
 * session while their account is still a stub (initiative overview, decision 4). The server refuses
 * every product route for them with 403 `SIGNUP_INCOMPLETE`; this is the client half of the same
 * rule, so the app sends them to the details step instead of painting a page that can only 403.
 *
 * The rule lives here alone: `App.tsx`'s three domain shells ask `isSignupIncomplete` before their
 * own company routing, the anonymous auth routes ask it in `beforeLoad`, and
 * `useIncompleteSignupGuard` performs the redirect from inside the app shell.
 *
 * Sign-out and account deletion stay reachable because they are actions rather than routes - the
 * details step carries the escape, and the backend allowlists both endpoints for an incomplete User.
 */

/** Narrowed to the single field the rule reads, so any User-shaped record can be asked. */
export interface IncompleteSignupSubject {
	incompleteSince: string | null;
}

export const isSignupIncomplete = (user: IncompleteSignupSubject | null | undefined): boolean =>
	Boolean(user?.incompleteSince);

/**
 * The one destination an incomplete User is allowed: the step that finishes their signup.
 *
 * The search comes from `socialDetailsStep()` rather than a literal, because the step the URL
 * names and the step `resolveSignupStep` will actually render have to be the same one. A signed-in
 * incomplete User holds no proof token, so the `details` step is never theirs: naming it here sent
 * them to the address form of an account they already own (review 1, B1).
 */
export const SIGNUP_DETAILS_ROUTE = { to: '/signup', search: socialDetailsStep() } as const;

/** True for the routes an incomplete User may stay on rather than be redirected off. */
export const isIncompleteSignupAllowedPath = (pathname: string): boolean =>
	pathname === '/signup' || pathname.startsWith('/signup/');

/**
 * Holds an incomplete User on the details step from inside the authenticated shell. Mounted once,
 * in the chrome all three domain shells share, so no shell carries its own copy of the rule.
 */
export const useIncompleteSignupGuard = (): void => {
	const navigate = useNavigate();
	const authenticated = useGlobalStoreSelector((s) => s.authenticated);
	const incomplete = useGlobalStoreSelector((s) => isSignupIncomplete(s.user));
	const pathname = useRouterState({ select: (state) => state.location.pathname });

	useEffect(() => {
		if (!authenticated || !incomplete) return;
		if (isIncompleteSignupAllowedPath(pathname)) return;
		void navigate(SIGNUP_DETAILS_ROUTE);
	}, [authenticated, incomplete, pathname, navigate]);
};
